import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recognitionsApi, SubmitRecognitionRequest } from '../api/recognitions';

export interface RecognitionFilters {
  brgId?: number;
  status?: string; // Status ID (1, 2, 3) as string
  recognitionTypeId?: number;
}

export const useRecognitions = (filters?: RecognitionFilters) => {
  return useQuery({
    queryKey: ['recognitions', filters],
    queryFn: () => recognitionsApi.list(filters),
    staleTime: 30000,
    retry: 1,
  });
};

export const useRecognitionById = (id: number | null) => {
  return useQuery({
    queryKey: ['recognition', id],
    queryFn: () => (id ? recognitionsApi.getById(id) : Promise.resolve(null)),
    enabled: !!id,
    staleTime: 30000,
    retry: 1,
  });
};

export const useRecognitionStats = () => {
  return useQuery({
    queryKey: ['recognitionStats'],
    queryFn: () => recognitionsApi.getStats(),
    staleTime: 60000,
    retry: 1,
  });
};

export const useRecognitionTypes = () => {
  return useQuery({
    queryKey: ['recognitionTypes'],
    queryFn: () => recognitionsApi.getTypes(),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
};

export const useRecognitionMutations = () => {
  const queryClient = useQueryClient();

  const submitRecognition = useMutation({
    mutationFn: (request: SubmitRecognitionRequest) => recognitionsApi.submit(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recognitions'] });
      queryClient.invalidateQueries({ queryKey: ['recognitionStats'] });
    },
  });

  const approveRecognition = useMutation({
    mutationFn: (id: number) => recognitionsApi.approve(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['recognition', id] });
      queryClient.invalidateQueries({ queryKey: ['recognitions'] });
      queryClient.invalidateQueries({ queryKey: ['recognitionStats'] });
    },
  });

  const denyRecognition = useMutation({
    mutationFn: (id: number) => recognitionsApi.deny(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['recognition', id] });
      queryClient.invalidateQueries({ queryKey: ['recognitions'] });
      queryClient.invalidateQueries({ queryKey: ['recognitionStats'] });
    },
  });

  return {
    submitRecognition,
    approveRecognition,
    denyRecognition,
  };
};

// Re-export for convenience
export type { SubmitRecognitionRequest };
